import { Button, Dialog, DialogProps } from '@highmobility/console-ui-components';
import { FC, useMemo } from 'react';
import { throttle } from 'radash';
import { UseFormReturn } from 'react-hook-form';
import { UseMutationResult } from '@tanstack/react-query';

import { MFACode } from '@/components/molecules/MFACode';

export type MFARecoveryDialogProps = {
  isOpen: DialogProps['isOpen'];
  setIsOpen: DialogProps['setIsOpen'];
  form: UseFormReturn<{ code: string }, any, { code: string }>;
  mutation: UseMutationResult<any, any, { code: string }, unknown>;
};

export const MFARecoveryDialog: FC<MFARecoveryDialogProps> = (props) => {
  const onSubmit = useMemo(
    () => {
      return throttle(
        { interval: 1000 },
        props.form.handleSubmit((data) => {
          props.mutation.mutate(data);
        })
      );
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onChangeCode = (value: string) => {
    if (value.length === 11) {
      props.form.setValue('code', value);
      onSubmit();
    }
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      setIsOpen={props.setIsOpen}
      title="Two-factor authentication"
      flexWidth
    >
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="pb-3 text-heading-1">Two-factor authentication</h1>
          <p className="font-bold">
            Enter one of the recovery codes you received when you activated two-factor
            authentication.
          </p>
        </div>
        <div>
          <form onSubmit={onSubmit}>
            <MFACode
              onChange={onChangeCode}
              error={props.form.formState.errors.code?.message}
              isRecoveryCode
            />
          </form>
        </div>
        <div className="flex justify-end">
          <Button intent="primary" onClick={onSubmit} disabled={props.mutation.isPending}>
            {props.mutation.isPending ? 'Verifying...' : 'Verify'}
          </Button>
        </div>
        <p className="font-bold text-white-60">
          Don&apos;t have access to your recovery codes? Either{' '}
          <span
            className="text-white hover:cursor-pointer hover:underline"
            onClick={() => props.setIsOpen(false)}
          >
            go back
          </span>{' '}
          and use your authentication app or{' '}
          <a
            href="https://www.high-mobility.com/support"
            target="_blank"
            rel="noreferrer"
            className="font-bold text-white hover:underline"
          >
            contact support
          </a>{' '}
          to recover your account.
        </p>
      </div>
    </Dialog>
  );
};
